import { DEFAULT_LOCALE, resolveSupportedLocale } from '../i18n/supportedLocales';
import { CURRENCIES as isoCurrencies } from '../resources/iso_currencies';

import currency = require('currency.js');

export interface IIsoCurrencies {
  country: string;
  name: string;
  alphabetic_code: string;
  numeric_code?: number | null;
  exponent?: number | null;
}

/** Options for {@link AbacusCurrency.toLocalizedString}. */
export interface LocalizedStringOptions extends Intl.NumberFormatOptions {
  /** Strip trailing `.0` decimals, matching {@link AbacusCurrency.toFormattedString}. */
  pruneZeroDecimals?: boolean;
}

const winstonKey = 'shared.helpers.currencyWrapper.abacusCurrency';
const defaultCurrencyCode = 'USD';
export const isoCurrenciesByCode: Record<string, IIsoCurrencies> = {};

isoCurrencies.forEach(function (isoCurrency) {
  if (isoCurrency.alphabetic_code) {
    isoCurrenciesByCode[isoCurrency.alphabetic_code] = isoCurrency;
  }
});

export class AbacusCurrency {
  currencyObject: currency;
  currency: IIsoCurrencies;
  locale?: string;

  /**
   * @param amount - numeric amount, or a currency.js instance
   * @param currencyCode - ISO 4217 code; defaults to `USD`
   * @param isAmountMajorUnits - when omitted or `false`, `amount` is in minor units (cents); when `true`, major units
   * @param precision - override decimal precision; defaults to the currency exponent
   * @param locale - BCP-47 locale for {@link toLocalizedString}
   */
  constructor(
    amount: number | string | currency,
    currencyCode?: string,
    isAmountMajorUnits?: boolean,
    precision?: number,
    locale?: string
  ) {
    if (!currencyCode) {
      currencyCode = defaultCurrencyCode;
    }

    if (!isoCurrenciesByCode[currencyCode]) {
      throw new Error(`expected valid currency code, received: ${currencyCode}`);
    }

    this.currency = isoCurrenciesByCode[currencyCode];
    this.locale = locale;
    const currency_precision = precision ?? this.currency.exponent ?? undefined;
    this.currencyObject = currency(amount, { fromCents: !isAmountMajorUnits, precision: currency_precision });
  }

  private getCurrencyObjectForAggregating(amount: currency.Any, currencyCode?: string, isAmountMajorUnits?: boolean) {
    const isUsingDefaultUSD = !currencyCode && this.currency.alphabetic_code === defaultCurrencyCode;

    // throw if the base currency code is not USD and currencyCode is either undefined, or provided currency code does not match the original currency code
    if (!isUsingDefaultUSD && currencyCode !== this.currency.alphabetic_code) {
      throw new Error(`${winstonKey}.addMinorAmount.CURRENCY_CODE_MUST_MATCH`);
    }
    return currency(amount, { fromCents: !isAmountMajorUnits, precision: this.currency.exponent ?? undefined });
  }
  getUnitValue = () => this.currencyObject.value;
  getMinorUnitValue = () => this.currencyObject.intValue;
  addMajorAmount(amount: currency.Any) {
    const currencyToAdd = this.getCurrencyObjectForAggregating(amount, this.currency.alphabetic_code, true);
    const newAmountObject = this.currencyObject.add(currencyToAdd);
    return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true, undefined, this.locale);
  }
  subtractMajorAmount(amount: currency.Any) {
    const currencyToSubtract = this.getCurrencyObjectForAggregating(amount, this.currency.alphabetic_code, true);

    const newAmountObject = this.currencyObject.subtract(currencyToSubtract);
    return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true, undefined, this.locale);
  }
  divide(divisor: number) {
    const newAmountObject = this.currencyObject.divide(divisor);

    return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true, undefined, this.locale);
  }
  multiply(multiple: number) {
    const newAmountObject = this.currencyObject.multiply(multiple);

    return new AbacusCurrency(newAmountObject.value, this.currency.alphabetic_code, true, undefined, this.locale);
  }

  /**
   * Converts the instance currency object given the exchange rate and provided currency code.
   * Use caution with logic using inverted rates (e.g. from param currency code -> instance currency object)
   * @see convertAmountToTargetCurrency
   * @param exchangeRate
   * @param currencyCode
   * @returns
   */
  convert(exchangeRate: number, currencyCode: string) {
    const targetCurrency = isoCurrenciesByCode[currencyCode];
    const currency_exponent = this.currency.exponent ?? 0;
    const intermediatePrecision = Math.max(currency_exponent, targetCurrency?.exponent ?? 0) + 4;

    // Use an intermediate currency.js object with extra precision so source-currency exponent
    // does not truncate target fractional units before target normalization.
    const intermediateAmount = currency(this.currencyObject.value, {
      fromCents: false,
      precision: intermediatePrecision
    }).multiply(exchangeRate);

    return new AbacusCurrency(intermediateAmount.value, currencyCode, true, undefined, this.locale);
  }

  split(numberOfSplits: number) {
    const splits = this.currencyObject.distribute(numberOfSplits);

    return splits.map((split) => new AbacusCurrency(split.value, this.currency.alphabetic_code, true, undefined, this.locale));
  }
  toFormattedString(pruneZeroDecimals: boolean = false) {
    const formattedString = this.currencyObject.format();
    if (pruneZeroDecimals) {
      return formattedString.replace(/\.0+$/, '');
    }

    return formattedString;
  }

  /**
   * Format this amount for the given locale using `Intl.NumberFormat`.
   *
   * @param locale - BCP-47 locale; falls back to the instance locale
   * @param options - optional Intl formatting options
   */
  toLocalizedString(locale?: string, options: LocalizedStringOptions = {}) {
    const { pruneZeroDecimals, ...numberFormatOptions } = options;
    const resolvedLocale = resolveSupportedLocale(locale ?? this.locale ?? DEFAULT_LOCALE);
    const formatted = new Intl.NumberFormat(resolvedLocale, {
      style: 'currency',
      currency: this.currency.alphabetic_code,
      ...numberFormatOptions
    }).format(this.getUnitValue());

    if (pruneZeroDecimals) {
      return formatted.replace(/\.0+$/, '');
    }

    return formatted;
  }

  // this will return the currency value in major units with decimal padding (no currency marker)
  getUnitStringValueWithDecimals() {
    return this.currencyObject.toString();
  }
  addMinorAmount = (amount: currency.Any, currencyCode?: string) =>
    this.addMajorAmount(this.getCurrencyObjectForAggregating(amount, currencyCode));
  subtractMinorAmount = (amount: currency.Any, currencyCode?: string) =>
    this.subtractMajorAmount(this.getCurrencyObjectForAggregating(amount, currencyCode));
}
